import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { TextField, Typography } from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';

import LoadingButton from '../../components/buttons/Buttons';

import {
  RequestDemoAccess,
  VerifyDemoCode,
} from '../../redux/actions/demoAccessActions';

import './styles.scss';

const CODE_LENGTH = 5;
const RESEND_COOLDOWN = 30; // seconds

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const STEPS = {
  IDENTITY: 'identity',
  VERIFY: 'verify',
  PENDING: 'pending',
  DONE: 'done',
};

const STRIP_TEXT = {
  [STEPS.IDENTITY]: 'Access request · Identity · Step 1 of 2',
  [STEPS.VERIFY]: 'Access request · Verification · Step 2 of 2',
  [STEPS.PENDING]: 'Access request · Under review · Response within 1 business day',
  [STEPS.DONE]: 'Access granted · Check your inbox',
};

const DemoAccessPage = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const isCertify = pathname.includes('certify');
  const eyebrow = isCertify
    ? 'Lusid Certify · Platform Demonstration'
    : 'LusidPay · Platform Demonstration';

  const [step, setStep] = useState(STEPS.IDENTITY);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [resendIn, setResendIn] = useState(0);

  // resend cooldown ticker
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedName) {
      setError('Please enter your name.');
      return;
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Please enter a valid work email address.');
      return;
    }

    const { statusCode, res } = await dispatch(
      RequestDemoAccess({ name: trimmedName, email: trimmedEmail }, setLoading)
    );

    if (statusCode === 200 || statusCode === 201) {
      setEmail(trimmedEmail);
      // Allowlisted domains get a code; everyone else goes to manual review.
      if (res?.data?.status === 'code_sent') {
        setStep(STEPS.VERIFY);
        setResendIn(RESEND_COOLDOWN);
      } else {
        setStep(STEPS.PENDING);
      }
    } else {
      setError(res?.message || 'Something went wrong on our side. Please try again.');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (code.length !== CODE_LENGTH) {
      setError(`Please enter all ${CODE_LENGTH} digits.`);
      return;
    }

    const { statusCode, res } = await dispatch(
      VerifyDemoCode({ email, code }, setLoading)
    );

    if (statusCode === 200) {
      setStep(STEPS.DONE);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setError(res?.message || "That code didn't work. Please request a new one.");
      setCode('');
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || loading) return;
    setError('');

    const { statusCode, res } = await dispatch(
      RequestDemoAccess({ name: name.trim() || 'resend', email }, setLoading)
    );

    if ((statusCode === 200 || statusCode === 201) && res?.data?.status === 'code_sent') {
      setResendIn(RESEND_COOLDOWN);
    } else if (statusCode === 200 || statusCode === 201) {
      setStep(STEPS.PENDING);
    } else {
      setError(res?.message || 'Could not send a new code. Please try again.');
    }
  };

  return (
    <div className="demo-access">
      <div className="demo-access__container">
        <div className="demo-access__hero">
          <p className="demo-access__eyebrow">{eyebrow}</p>
          <h1 className="demo-access__title">Platform Demonstration</h1>
          <p className="demo-access__subtitle">
            A guided look at the applicant-facing assistance co-pilot and the
            certifier-facing adjudication dashboard.
          </p>
        </div>

        <div className="demo-access__panel">
          <div
            className={`demo-access__strip ${
              step === STEPS.DONE ? 'demo-access__strip--granted' : ''
            }`}
            aria-live="polite"
          >
            {STRIP_TEXT[step]}
          </div>

          <div className="demo-access__body">
            {error && (
              <div className="demo-access__msg demo-access__msg--error">{error}</div>
            )}

            {/* ---------- Step 1 · Identity ---------- */}
            {step === STEPS.IDENTITY && (
              <form className="demo-access__form" onSubmit={handleRequestAccess} noValidate>
                <p className="demo-access__lede">
                  Enter your name and work email to request access. We&apos;ll send a
                  verification code to your inbox.
                </p>

                <TextField
                  label="Name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Okonkwo"
                  variant="outlined"
                  fullWidth
                  required
                  autoComplete="name"
                  disabled={loading}
                />

                <TextField
                  label="Work email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane.okonkwo@agency.gov"
                  variant="outlined"
                  fullWidth
                  required
                  autoComplete="email"
                  disabled={loading}
                />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  loading={loading}
                  className="demo-access__submit"
                >
                  Request access
                </LoadingButton>
              </form>
            )}

            {/* ---------- Step 2 · Verify ---------- */}
            {step === STEPS.VERIFY && (
              <form className="demo-access__form" onSubmit={handleVerify} noValidate>
                <p className="demo-access__lede">
                  We sent a {CODE_LENGTH}-digit code to <strong>{email}</strong>. It
                  expires in 15 minutes.
                </p>

                <TextField
                  className="demo-access__code"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
                  }
                  placeholder={'0'.repeat(CODE_LENGTH)}
                  variant="outlined"
                  fullWidth
                  autoComplete="one-time-code"
                  disabled={loading}
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: CODE_LENGTH,
                    'aria-label': 'Access code',
                  }}
                />

                <LoadingButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  loading={loading}
                  className="demo-access__submit"
                >
                  Verify and continue
                </LoadingButton>

                <button
                  type="button"
                  className="demo-access__resend"
                  onClick={handleResend}
                  disabled={resendIn > 0 || loading}
                >
                  {resendIn > 0 ? `Send a new code (${resendIn}s)` : 'Send a new code'}
                </button>
              </form>
            )}

            {/* ---------- Step · Under review ---------- */}
            {step === STEPS.PENDING && (
              <div className="demo-access__done">
                <MarkEmailReadOutlinedIcon className="demo-access__done-icon" />
                <Typography variant="h4" className="demo-access__done-title">
                  Request received
                </Typography>
                <p className="demo-access__done-text">
                  Your domain isn&apos;t on our verified list, so this one goes to manual
                  review. You&apos;ll hear back within one business day at{' '}
                  <strong>{email}</strong>.
                </p>
                <p className="demo-access__done-sub">
                  Questions in the meantime? Write to{' '}
                  <a href="mailto:sales@lusidpay.com">sales@lusidpay.com</a>.
                </p>
              </div>
            )}

            {/* ---------- Step · Done ---------- */}
            {step === STEPS.DONE && (
              <div className="demo-access__done">
                <MarkEmailReadOutlinedIcon className="demo-access__done-icon" />
                <Typography variant="h4" className="demo-access__done-title">
                  You&apos;re verified
                </Typography>
                <p className="demo-access__done-text">
                  We&apos;ve emailed secure links to the two demonstration videos to{' '}
                  <strong>{email}</strong>. The links expire in 10 minutes — if they
                  lapse, just verify again for fresh ones.
                </p>
                <p className="demo-access__done-sub">
                  Didn&apos;t get it? Check your spam folder, or write to{' '}
                  <a href="mailto:sales@lusidpay.com">sales@lusidpay.com</a>.
                </p>
              </div>
            )}
          </div>
        </div>

        <p className="demo-access__privacy">
          We use your name and email only to verify access and to know who has seen the
          demonstration. No marketing lists, no third parties.
        </p>
      </div>
    </div>
  );
};

export default DemoAccessPage;
